// S2-FE2 + S2-LIB1: MetricCard
// S2-LIB1 adds optional `size` prop ('sm' | 'md' | 'hero') for larger
// display contexts (e.g. hero numbers on mission-control pages).
// Default size='sm' preserves existing visual behavior exactly.
import './MetricCard.css';

export interface MetricCardProps {
  value: string | number;
  label: string;
  status: 'pass' | 'fail' | 'warn' | 'neutral';
  mono?: boolean;
  /**
   * Visual size variant. Defaults to 'sm' to preserve current behavior
   * for every existing caller (no call site changes required).
   * - 'sm'   (default) original compact metric tile
   * - 'md'   larger value (20px / xl), for secondary hero areas
   * - 'hero' display-size number (36px / 4xl) in mono — used for
   *          headline KPIs. Always renders mono regardless of `mono` prop.
   */
  size?: 'sm' | 'md' | 'hero';
}

export function MetricCard({
  value,
  label,
  status,
  mono = false,
  size = 'sm',
}: MetricCardProps) {
  // Hero is inherently mono (display KPI numeric). Force mono on regardless
  // of the caller's `mono` flag so hero always looks uniform.
  const effectiveMono = mono || size === 'hero';

  // Card-root class list. Only append the size modifier when non-default
  // so existing snapshots / DOM output stay identical for size='sm'.
  const cardClassName = [
    'airgent-metric-card',
    size !== 'sm' ? `airgent-metric-card-${size}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const valueClassName = [
    'airgent-metric-value',
    `airgent-metric-value-${status}`,
    effectiveMono ? 'airgent-metric-value-mono' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClassName}>
      <div className={valueClassName}>{value}</div>
      <div className="airgent-metric-label">{label}</div>
    </div>
  );
}
